import type {
  BankAccount,
  CreateBankAccountPayload,
  UpdateBankAccountPayload,
} from "../repositories/bankRepository";
import type { FormChatState } from "../repositories/formChat.repository";

type BankAccountChatValues = {
  name: string;
  bankAccountOptionId: string;
  agency: string;
  account: string;
  pixKey: string;
  pixKeyType: BankAccount["pixKeyType"];
  currentBalance: number;
};

const readValues = (state: FormChatState): BankAccountChatValues | null => {
  const { bankAccountOptionId, bankName, agency, account, pixKey, pixKeyType } =
    state;
  const currentBalance =
    typeof state.currentBalance === "number" &&
    Number.isFinite(state.currentBalance)
      ? state.currentBalance
      : 0;
  if (
    !bankAccountOptionId ||
    !bankName ||
    !agency?.trim() ||
    !account?.trim() ||
    !pixKey?.trim() ||
    !pixKeyType
  ) {
    return null;
  }
  return {
    name: bankName,
    bankAccountOptionId,
    agency: agency.trim(),
    account: account.trim(),
    pixKey: pixKey.trim(),
    pixKeyType: pixKeyType as BankAccount["pixKeyType"],
    currentBalance,
  };
};

/** Same body CreateFinancialAccount posts to /bank-account; null when the chat state is incomplete. */
export const buildBankAccountCreatePayload = (
  state: FormChatState,
  owner: { terreiroId?: string; mediumId?: string },
): CreateBankAccountPayload | null => {
  if (state.accountType === "CASH") return null;
  const values = readValues(state);
  if (!values) return null;
  return {
    ...(owner.terreiroId ? { terreiroId: owner.terreiroId } : {}),
    ...(!owner.terreiroId && owner.mediumId ? { mediumId: owner.mediumId } : {}),
    ...values,
    initialBalance: values.currentBalance,
    accountType: "BANK",
  };
};

/** Same body EditFinancialAccount puts to /bank-account/:id; null when the chat state is incomplete. */
export const buildBankAccountUpdatePayload = (
  state: FormChatState,
): UpdateBankAccountPayload | null => {
  if (state.accountType === "CASH") {
    if (!state.bankName || typeof state.currentBalance !== "number") return null;
    return {
      name: state.bankName,
      account: "",
      agency: "",
      bankAccountOptionId: state.bankAccountOptionId,
      currentBalance: String(state.currentBalance),
      pixKey: "",
      pixKeyType: "PHONE",
    };
  }
  const values = readValues(state);
  if (!values) return null;
  return { ...values, currentBalance: String(values.currentBalance) };
};
